import { Injectable } from '@angular/core';
import { DashboardSite } from 'app/_models/dashboard-site.model';
import { DashboardWorkOrderGraphData } from 'app/_models/dashboard-work-order-graph-data.model';
import { LiveSession } from 'app/_models/live-session.model';
import { SessionHealthCheckInfo } from 'app/_models/session-health-check-info.model';
import { SiteAsset } from 'app/_models/site-asset.model';
import { WorkOrderState } from 'app/_models/work-order-state';
import { LiveSessionService } from 'app/_services/live-session.service';
import { SiteWorkOrdersService } from 'app/_services/site-work-orders.service';
import { SiteService } from 'app/_services/site.service';
import { WorkOrderStateService } from 'app/_services/work-order-state.service';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// tslint:disable:prefer-const many variables in this file are mutated, but not reassigned, 'let' seems more appropriate
@Injectable()
export class DashboardService {
  private cancelAllSubscriptions = new Subject<void>();

  private sites = new ReplaySubject<DashboardSite[]>(1);
  private allSitesSummary = new ReplaySubject<DashboardSummaryInfo>(1);
  private healthChecksInfo = new ReplaySubject<DashboardHealthCheckInfo>(1);
  private allSitePendingWorkOrders = new ReplaySubject<DashboardAllSitePendingWorkOrders>(1);

  private workOrderGraphDataGroupedBySite = new ReplaySubject<DashboardWorkOrderGraphDataGroupedBySite>(1);
  private sessionsGroupedBySite = new ReplaySubject<DashboardSessionsGroupedBySite>(1);

  constructor(
    private liveSessionService: LiveSessionService,
    private siteService: SiteService,
    private workOrderService: SiteWorkOrdersService,
    private workOrderStateService: WorkOrderStateService,
  ) {
  }

  public cancelSubscriptions() {
    if (this.cancelAllSubscriptions) {
      this.cancelAllSubscriptions.next();
      this.cancelAllSubscriptions.complete();
    }
  }

  private restartTheCancelSubscriptionOptionIfNecessary() {
    if (this.cancelAllSubscriptions.isStopped) {
      this.cancelAllSubscriptions = new Subject<void>();
    }
  }

  // reset the ReplaySubjects on logout, so that logging in doesnt show other user data
  public clearCache() {
    this.sites = new ReplaySubject<DashboardSite[]>(1);
    this.allSitesSummary = new ReplaySubject<DashboardSummaryInfo>(1);
    this.healthChecksInfo = new ReplaySubject<DashboardHealthCheckInfo>(1);
    this.allSitePendingWorkOrders = new ReplaySubject<DashboardAllSitePendingWorkOrders>(1);

    this.workOrderGraphDataGroupedBySite = new ReplaySubject<DashboardWorkOrderGraphDataGroupedBySite>(1);
    this.sessionsGroupedBySite = new ReplaySubject<DashboardSessionsGroupedBySite>(1);
  }

  // Observables

  public sitesObservable(): Observable<DashboardSite[]> {
    this.restartTheCancelSubscriptionOptionIfNecessary();
    return this.sites.pipe(takeUntil(this.cancelAllSubscriptions));
  }

  public liveSessionsObservable(): Observable<DashboardSummaryInfo> {
    this.restartTheCancelSubscriptionOptionIfNecessary();
    return this.allSitesSummary.pipe(takeUntil(this.cancelAllSubscriptions));
  }

  public sessionsGroupedBySiteObservable(): Observable<DashboardSessionsGroupedBySite> {
    this.restartTheCancelSubscriptionOptionIfNecessary();
    return this.sessionsGroupedBySite.pipe(takeUntil(this.cancelAllSubscriptions));
  }

  public healthChecksDueObservable(): Observable<DashboardHealthCheckInfo> {
    this.restartTheCancelSubscriptionOptionIfNecessary();
    return this.healthChecksInfo.pipe(takeUntil(this.cancelAllSubscriptions));
  }

  public allSitePendingWorkOrdersObservable(): Observable<DashboardAllSitePendingWorkOrders> {
    this.restartTheCancelSubscriptionOptionIfNecessary();
    return this.allSitePendingWorkOrders.pipe(takeUntil(this.cancelAllSubscriptions));
  }

  public workOrderGraphDataGroupedBySiteObservable(): Observable<DashboardWorkOrderGraphDataGroupedBySite> {
    this.restartTheCancelSubscriptionOptionIfNecessary();
    return this.workOrderGraphDataGroupedBySite.pipe(takeUntil(this.cancelAllSubscriptions));
  }

  // fetch/refresh functions
  public fetchSites() {
    this.siteService.getDashboardSummaryBySite().subscribe(sitesData => {
      const sites = [];

      sitesData.forEach(siteData => {
        const site = new DashboardSite().deserialize(siteData);
        sites.push(site);
      });

      this.sites.next(sites);
    });
  }

  // populates both the All Sites summary card and
  // each site's Workers card
  public fetchLiveSessions() {
    this.liveSessionService.getAllLiveSessionsNoPaginationMapped().subscribe(liveSessions => {

      // Health Check card
      let healthChecksDue = [];
      liveSessions.forEach(session => {
        if (session.isHealthCheckDue()) {
          healthChecksDue.push(session);
        }
      });
      const healthChecksMapped = healthChecksDue.map(session => new SessionHealthCheckInfo().deserialize(session, 'WAYPOINT'));

      // Expected Check Off Time card
      let checkOffsDue = [];
      liveSessions.forEach(session => {
        if (session.isExpectedCheckOffDue()) {
          checkOffsDue.push(session);
        }
      });

      const expectedCheckOffTimesMapped = checkOffsDue.map(session => new SessionHealthCheckInfo().deserialize(session, 'EXPECTED_CHECK_OFF'));

      this.healthChecksInfo.next({
        loading: false,
        healthChecksDue: healthChecksMapped,
        expectedCheckOffTimesDue: expectedCheckOffTimesMapped,
      });

      // All Sites summary card
      let workOrderSessions = [];
      let siteSessions = [];
      let workers = [];
      let guestCount = 0;

      // each site's Workers card
      let sessionsGroupedBySite: DashboardSessionsGroupedBySite = { loading: false };

      // for each live session, set up both the All Sites card and the individual sites' cards
      liveSessions.forEach(session => {
        const siteId = session.siteId;
        // if not already existing add defaults
        if (!sessionsGroupedBySite[siteId]) {
          sessionsGroupedBySite[siteId] = {
            siteSessions: [],
            workersCheckedOnToWorkOrders: [],
            guestCount: 0
          };
        }

        workers.push(session);
        guestCount += session.guestCount;
        sessionsGroupedBySite[siteId].guestCount += session.guestCount;

        if (session.isWorkOrderSession()) {
          if (session.owner) { // ignore 'addtional workers' sessions
            workOrderSessions.push(session);
          }
          sessionsGroupedBySite[siteId].workersCheckedOnToWorkOrders.push(session); // include 'additonal workers' sessions
        } else {
          // site session
          siteSessions.push(session);
          sessionsGroupedBySite[siteId].siteSessions.push(session);
        }
      });

      this.allSitesSummary.next({
        loading: false,
        workOrderSessions: workOrderSessions,
        siteSessions: siteSessions,
        workers: workers,
        guestCount: guestCount,
      });


      this.sessionsGroupedBySite.next(sessionsGroupedBySite);
    });
  }

  public fetchWorkOrders() {
    this.workOrderService.getWorkOrdersForDash().subscribe(allWorkOrdersData => {
      const workOrderGraphDataGroupedBySite = { loading: false };

      const allSitePendingWorkOrders = [];
      allWorkOrdersData.body.forEach(w => {
        w.state = this.workOrderStateService.setWorkOrderState(w);
        if (w.state === WorkOrderState.Pending) {
          allSitePendingWorkOrders.push(w);
        }
      });

      this.allSitePendingWorkOrders.next({
        loading: false,
        workOrders: allSitePendingWorkOrders,
      });

      const workOrders = this.groupWorkOrdersBySiteId(allWorkOrdersData.body);

      Object.keys(workOrders).forEach(siteId => {
        const workOrdersForSite = workOrders[siteId];
        const groupedWorkOrders = this.workOrderStateService.groupByState(workOrdersForSite);

        const siteWorkOrderGraphData: DashboardWorkOrderGraphData[] = [];
        // map to key/value format for graph
        Object.keys(groupedWorkOrders).forEach(key => {
          const group = groupedWorkOrders[key];

          // if there are no work orders in a group, don't show them in the graph
          if (group.length > 0) {
            const workOrderGraphData = new DashboardWorkOrderGraphData().deserialize({
              key: group[0].state,
              value: group.length,
              data: group,
            });
            siteWorkOrderGraphData.push(workOrderGraphData);
          }
        });

        // sorts the graph's bars, top to bottom, in a consistent order
        siteWorkOrderGraphData.sort((a, b) => this.workOrderStateService.compareByDisplayOrder(a.key, b.key));
        workOrderGraphDataGroupedBySite[siteId] = siteWorkOrderGraphData;
      });

      this.workOrderGraphDataGroupedBySite.next(workOrderGraphDataGroupedBySite);
    });
  }

  private groupWorkOrdersBySiteId(workOrders: any[]) {
    return workOrders.reduce((groupedWorkOrders, workOrder) => {
      const siteId = workOrder.siteId;

      if (!groupedWorkOrders[siteId]) {
        groupedWorkOrders[siteId] = [];
      }
      groupedWorkOrders[siteId].push(workOrder);
      return groupedWorkOrders;
    }, {});
  }


}

export interface DashboardSummaryInfo {
  loading: boolean,
  workOrderSessions: LiveSession[];
  siteSessions: LiveSession[];
  workers: LiveSession[];
  guestCount: number;
}

export interface DashboardHealthCheckInfo {
  loading: boolean;
  healthChecksDue: SessionHealthCheckInfo[];
  expectedCheckOffTimesDue: SessionHealthCheckInfo[];
}

export interface DashboardAllSitePendingWorkOrders {
  loading: boolean;
  workOrders: any[];
}

export interface DashboardSessionsGroupedBySite {
  loading: boolean,
  [siteId: number]: {
    siteSessions: LiveSession[];
    workersCheckedOnToWorkOrders: LiveSession[];
    guestCount: number;
  }
}

export interface DashboardWorkOrderGraphDataGroupedBySite {
  loading: boolean,
  [siteId: number]: DashboardWorkOrderGraphData[];
}
